import { ValidationError } from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

import { Pattern as PolicyPattern } from './pattern'
import { ResourceType } from './resource-type'
import { SafeId } from './safe-id'

declare const TEMPLATE_BRAND: unique symbol

/**
 * The name of one concrete resource, built from its business IDs, e.g.
 * `atn:tenant_t_a:zone_z_1:app_a_dms`.
 *
 * Produce one with {@link Urn.from}. The format is part of the API, so
 * changing it is a breaking change.
 *
 * @see {@link Urn.Pattern} for wildcards
 * @see {@link Urn.Template} for scope placeholders
 * @see {@link Urn.Parsed} for the structured form
 */
export type Urn = `${typeof Urn.PREFIX}${string}`
export namespace Urn {
  export const PREFIX = `atn${PolicyPattern.SEPARATOR}` as const

  /**
   * What a policy statement may name. `*` alone, a URN, or a URN ending in
   * `:*` — which covers that resource **and** everything beneath it (unlike
   * IAM, where `:*` is descendants only).
   *
   * Wildcards are trailing and whole-segment only: `atn:tenant_*` and
   * `atn:tenant_*:zone_1` are invalid. Enforced by validation, not the type.
   */
  export type Pattern = typeof PolicyPattern.WILDCARD | Urn

  /**
   * What a **role's** statement may name: a {@link Pattern} that may also
   * contain `${scope.<variable>}` placeholders.
   *
   * Branded so that a role's statements cannot be passed where concrete ones
   * are required — the only way from here to a {@link Pattern} is
   * substituting an assignment's scope.
   */
  export type Template =
    | Pattern
    | (string & { readonly [TEMPLATE_BRAND]: true })

  /**
   * The structured form of a {@link Urn}. Generic over the ID type so that
   * {@link from} can accept plain entity IDs, while {@link parse} returns
   * proven {@link SafeId}s.
   */
  export type Parsed<TId extends string = SafeId> =
    | Parsed.Tenant<TId>
    | Parsed.Zone<TId>
    | Parsed.App<TId>
    | Parsed.Scenario<TId>
    | Parsed.Page<TId>
    | Parsed.Agent<TId>
    | Parsed.User<TId>
  export namespace Parsed {
    /** `atn:tenant_<tenantId>` */
    export interface Tenant<TId extends string = SafeId> {
      readonly type: ResourceType.Tenant
      readonly tenantId: TId
    }
    /** `atn:tenant_<tenantId>:zone_<zoneId>` */
    export interface Zone<TId extends string = SafeId> {
      readonly type: ResourceType.Zone
      readonly tenantId: TId
      readonly zoneId: TId
    }
    /** `atn:tenant_<tenantId>:zone_<zoneId>:app_<appId>` */
    export interface App<TId extends string = SafeId> {
      readonly type: ResourceType.App
      readonly tenantId: TId
      readonly zoneId: TId
      readonly appId: TId
    }
    /** `atn:tenant_<tenantId>:zone_<zoneId>:app_<appId>:scenario_<scenarioId>` */
    export interface Scenario<TId extends string = SafeId> {
      readonly type: ResourceType.Scenario
      readonly tenantId: TId
      readonly zoneId: TId
      readonly appId: TId
      readonly scenarioId: TId
    }
    /** `atn:tenant_<tenantId>:zone_<zoneId>:app_<appId>:page_<pageId>` */
    export interface Page<TId extends string = SafeId> {
      readonly type: ResourceType.Page
      readonly tenantId: TId
      readonly zoneId: TId
      readonly appId: TId
      readonly pageId: TId
    }
    /**
     * `atn:tenant_<tenantId>:agent_<agentId>` for a tenant-wide agent,
     * `atn:tenant_<tenantId>:zone_<zoneId>:agent_<agentId>` for one zone, or
     * `atn:tenant_<tenantId>:zone_<zoneId>:app_<appId>:agent_<agentId>` for
     * one app. An `appId` requires its `zoneId`
     *
     * @see {@link agentCandidates}
     */
    export interface Agent<TId extends string = SafeId> {
      readonly type: ResourceType.Agent
      readonly tenantId: TId
      readonly zoneId?: TId
      readonly appId?: TId
      readonly agentId: TId
    }
    /**
     * `atn:tenant_<tenantId>:user_<userId>` — a **membership, not an
     * identity**. A user in three tenants has three of these. It exists so
     * that "may grant to members of Acme" is an ordinary statement over
     * `atn:tenant_<acme>:user_*`.
     */
    export interface User<TId extends string = SafeId> {
      readonly type: ResourceType.User
      readonly tenantId: TId
      readonly userId: TId
    }
  }

  /** The ID field each segment type carries in {@link Parsed} */
  const ID_FIELD = {
    [ResourceType.Tenant]: 'tenantId',
    [ResourceType.Zone]: 'zoneId',
    [ResourceType.App]: 'appId',
    [ResourceType.Scenario]: 'scenarioId',
    [ResourceType.Page]: 'pageId',
    [ResourceType.Agent]: 'agentId',
    [ResourceType.User]: 'userId',
  } as const satisfies Record<ResourceType, string>

  /**
   * Every valid sequence of segment types. The last entry is the resource's
   * own type. Anything not listed here is not a URN.
   */
  const FORMS: readonly (readonly ResourceType[])[] = [
    [ResourceType.Tenant],
    [ResourceType.Tenant, ResourceType.Zone],
    [ResourceType.Tenant, ResourceType.Zone, ResourceType.App],
    [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Scenario,
    ],
    [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Page,
    ],
    [ResourceType.Tenant, ResourceType.Agent],
    [ResourceType.Tenant, ResourceType.Zone, ResourceType.Agent],
    [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Agent,
    ],
    [ResourceType.Tenant, ResourceType.User],
  ]

  /** Segment types of the longest form for each resource type */
  const PATHS = {
    [ResourceType.Tenant]: [ResourceType.Tenant],
    [ResourceType.Zone]: [ResourceType.Tenant, ResourceType.Zone],
    [ResourceType.App]: [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
    ],
    [ResourceType.Scenario]: [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Scenario,
    ],
    [ResourceType.Page]: [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Page,
    ],
    [ResourceType.Agent]: [
      ResourceType.Tenant,
      ResourceType.Zone,
      ResourceType.App,
      ResourceType.Agent,
    ],
    [ResourceType.User]: [ResourceType.Tenant, ResourceType.User],
  } as const satisfies Record<ResourceType, readonly ResourceType[]>

  /**
   * The segments of its path a resource type may omit — an agent's zone and
   * app. What is left must still be one of the {@link FORMS}
   */
  const OPTIONAL_SEGMENTS: Partial<
    Record<ResourceType, readonly ResourceType[]>
  > = {
    [ResourceType.Agent]: [ResourceType.Zone, ResourceType.App],
  }

  function isKnownForm(types: readonly ResourceType[]): boolean {
    return FORMS.some(
      form =>
        form.length === types.length && form.every((t, i) => t === types[i]),
    )
  }

  export const isUrn: TypeChecker<Urn> = TypeChecker.Factory.custom(
    (val: unknown): val is Urn =>
      typeof val === 'string' && parse(val) !== undefined,
    { typeDescription: 'policy.Urn' },
  )

  /**
   * The {@link Urn} naming the given resource.
   *
   * @throws {ValidationError} if any ID is not a {@link SafeId}, or the IDs
   * given form no valid path — an agent's `appId` without its `zoneId`
   */
  export function from(parsed: Parsed<string>): Urn {
    const fields = parsed as unknown as Readonly<Record<string, unknown>>
    const optional = OPTIONAL_SEGMENTS[parsed.type] ?? []
    const present = PATHS[parsed.type].filter(
      segmentType =>
        fields[ID_FIELD[segmentType]] !== undefined ||
        !optional.includes(segmentType),
    )
    if (!isKnownForm(present)) {
      throw new ValidationError({
        reason: `${withArticle(parsed.type)} URN cannot be formed from these IDs`,
        type: 'resource reference',
        value: parsed,
      })
    }
    const segments = present.map(segmentType => {
      const id = fields[ID_FIELD[segmentType]]
      if (!SafeId.isSafeId(id)) {
        throw new ValidationError({
          reason: `IDs in a URN must match ${SafeId.PATTERN}`,
          type: `${ID_FIELD[segmentType]} for a ${parsed.type} URN`,
          value: id,
        })
      }
      return `${segmentType}_${id}`
    })
    return `${PREFIX}${segments.join(PolicyPattern.SEPARATOR)}`
  }

  /**
   * Parses a concrete {@link Urn}. Returns `undefined` for anything that is
   * not one, including every wildcard and template form.
   */
  export function parse(value: string): Parsed | undefined {
    if (!value.startsWith(PREFIX)) {
      return undefined
    }
    const types: ResourceType[] = []
    const fields: Record<string, SafeId> = {}
    for (const segment of value
      .slice(PREFIX.length)
      .split(PolicyPattern.SEPARATOR)) {
      const separator = segment.indexOf('_')
      if (separator <= 0) {
        return undefined
      }
      const type = segment.slice(0, separator)
      const id = segment.slice(separator + 1)
      if (!ResourceType.isResourceType(type) || !SafeId.isSafeId(id)) {
        return undefined
      }
      types.push(type)
      fields[ID_FIELD[type]] = id
    }
    if (!isKnownForm(types)) {
      return undefined
    }
    return { ...fields, type: types[types.length - 1] } as unknown as Parsed
  }

  /**
   * A resource named by its entity IDs rather than by a URN string, so the URN
   * grammar never leaves the backend. The resource's type is the most specific
   * ID present. A client's context — tenant, zone, app, scenario — is one.
   */
  export interface Ref {
    readonly tenantId: string
    readonly zoneId?: string
    readonly appId?: string
    readonly scenarioId?: string
    readonly pageId?: string
    readonly agentId?: string
    readonly userId?: string
  }

  /**
   * The resource type a {@link Ref} names: its most specific ID. `agentId` and
   * `userId` come first because they sit beside the hierarchy, not in it
   */
  const REF_LEAF_ORDER: readonly ResourceType[] = [
    ResourceType.Agent,
    ResourceType.User,
    ResourceType.Scenario,
    ResourceType.Page,
    ResourceType.App,
    ResourceType.Zone,
    ResourceType.Tenant,
  ]

  /** `An agent`, `A zone` — for error messages */
  function withArticle(type: ResourceType): string {
    return `${/^[aeiou]/.test(type) ? 'An' : 'A'} ${type}`
  }

  /**
   * Builds the {@link Urn} a {@link Ref} names.
   *
   * Throws a {@link ValidationError} unless the IDs form exactly one valid
   * path: every ID the type requires present, and no ID it does not take — a
   * `scenarioId` without an `appId`, or an `agentId` beside an `appId`, names
   * nothing
   */
  export function fromRef(ref: Ref): Urn {
    const ids = ref as unknown as Readonly<Record<string, string | undefined>>
    const type =
      REF_LEAF_ORDER.find(t => ids[ID_FIELD[t]] !== undefined) ??
      ResourceType.Tenant
    const allowed = new Set<string>(PATHS[type].map(t => ID_FIELD[t]))
    const unexpected = Object.keys(ids).filter(
      key => ids[key] !== undefined && !allowed.has(key),
    )
    if (unexpected.length > 0) {
      throw new ValidationError({
        reason: `${withArticle(type)} does not take ${unexpected.join(', ')}`,
        type: 'resource reference',
        value: ref,
      })
    }
    const optional = OPTIONAL_SEGMENTS[type] ?? []
    const missing = PATHS[type]
      .filter(segmentType => !optional.includes(segmentType))
      .map(segmentType => ID_FIELD[segmentType])
      .filter(field => ids[field] === undefined)
    if (missing.length > 0) {
      throw new ValidationError({
        reason: `${withArticle(type)} requires ${missing.join(', ')}`,
        type: 'resource reference',
        value: ref,
      })
    }
    return from({ ...ids, type } as unknown as Parsed<string>)
  }

  /**
   * A resource named relative to a context: its type, and its ID when it is
   * not the context's own resource at that level
   */
  export interface Target {
    readonly type: ResourceType
    readonly id?: string
  }

  /**
   * Builds the {@link Urn} for a resource named relative to a client's current
   * context — the IDs of the tenant, zone, app and scenario the page is in.
   *
   * - no target → the context's own resource: its most specific ID
   * - `{ type, id }` → the resource of that type with that ID, under the
   *   context's ancestors of that type. `{ type: 'app', id }` in a zone context
   *   is an app in that zone
   * - `{ type }` alone → the context's own resource at that level.
   *   `{ type: 'tenant' }` in a zone context is the zone's tenant
   *
   * Context IDs below the target's level are ignored, so an app page can ask
   * about its zone. Throws a {@link ValidationError} when the context lacks
   * an ancestor the target needs
   */
  export function fromContext(context: Ref, target?: Target): Urn {
    if (!target) {
      return fromRef(context)
    }
    const contextIds = context as unknown as Readonly<
      Record<string, string | undefined>
    >
    const field = ID_FIELD[target.type]
    const ids: Record<string, string | undefined> = {}
    for (const segmentType of PATHS[target.type]) {
      ids[ID_FIELD[segmentType]] = contextIds[ID_FIELD[segmentType]]
    }
    if (target.id !== undefined) {
      ids[field] = target.id
    } else if (ids[field] === undefined) {
      throw new ValidationError({
        reason: `The context has no ${target.type}; give its id`,
        type: 'resource reference',
        value: { context, target },
      })
    }
    return fromRef(ids as unknown as Ref)
  }

  /**
   * Whether the context has its own resource of `type` — an ID at that level —
   * so `{ type }` alone names it through {@link fromContext}
   */
  export function reaches(context: Ref, type: ResourceType): boolean {
    const contextIds = context as unknown as Readonly<
      Record<string, string | undefined>
    >
    return contextIds[ID_FIELD[type]] !== undefined
  }

  /**
   * The deepest resource of the context that a resource of `type` sits
   * beneath: the context's IDs on that type's path, above the type itself.
   * Context IDs off the path are ignored, as {@link fromContext} does — a page
   * in a scenario context is looked for in the scenario's app.
   *
   * @throws {ValidationError} for a tenant, which nothing contains, or when
   * the context's IDs on the path form no valid one
   */
  export function scopeOf(context: Ref, type: ResourceType): Urn {
    const contextIds = context as unknown as Readonly<
      Record<string, string | undefined>
    >
    const above = PATHS[type].filter(segmentType => segmentType !== type)
    if (!above.length) {
      throw new ValidationError({
        reason: `${withArticle(type)} is beneath no other resource`,
        type: 'resource reference',
        value: { context, type },
      })
    }
    const ids = Object.fromEntries(
      above.map(segmentType => [
        ID_FIELD[segmentType],
        contextIds[ID_FIELD[segmentType]],
      ]),
    )
    return fromRef(ids as unknown as Ref)
  }

  /**
   * The resource types on the path to `type`, outermost first and ending with
   * `type`. A resource of `type` can lie beneath one of each of the others
   */
  export function pathOf(type: ResourceType): readonly ResourceType[] {
    return PATHS[type]
  }

  /**
   * The type and own ID of the resource a concrete {@link Urn} names, or
   * `undefined` if it is not one
   */
  export function leafOf(
    urn: Urn,
  ): { readonly type: ResourceType; readonly id: SafeId } | undefined {
    const parsed = parse(urn)
    if (!parsed) {
      return undefined
    }
    const fields = parsed as unknown as Readonly<Record<string, SafeId>>
    return { id: fields[ID_FIELD[parsed.type]], type: parsed.type }
  }

  /**
   * Every proper ancestor of the given {@link Urn}, nearest first.
   *
   * @example
   * ancestors('atn:tenant_t_a:zone_z_1:app_a_1')
   * // ['atn:tenant_t_a:zone_z_1', 'atn:tenant_t_a']
   */
  export function ancestors(urn: Urn): readonly Urn[] {
    const segments = urn.slice(PREFIX.length).split(PolicyPattern.SEPARATOR)
    const result: Urn[] = []
    for (let length = segments.length - 1; length > 0; length--) {
      result.push(
        `${PREFIX}${segments.slice(0, length).join(PolicyPattern.SEPARATOR)}`,
      )
    }
    return result
  }

  /**
   * The tenant a {@link Urn} or {@link Pattern} falls under, or `undefined`
   * when it names no single tenant (`*`, `atn:*`).
   *
   * Decides which reverse-index partition a grant is written to, and which
   * user URNs a delegation check requires.
   */
  export function tenantOf(pattern: Pattern): SafeId | undefined {
    if (!pattern.startsWith(PREFIX)) {
      return undefined
    }
    const [first] = pattern.slice(PREFIX.length).split(PolicyPattern.SEPARATOR)
    const tenantPrefix = `${ResourceType.Tenant}_`
    if (!first.startsWith(tenantPrefix)) {
      return undefined
    }
    const id = first.slice(tenantPrefix.length)
    return SafeId.isSafeId(id) ? id : undefined
  }

  /**
   * The URNs an agent request may be granted through, broadest first: the
   * tenant-wide agent, then — as far as the context goes — the agent in its
   * zone and in its app.
   *
   * An agent can be granted at any of those levels, and a wildcard cannot
   * express "this agent in any zone" (`zone_*` is mid-string). So enforcement
   * and member listings both check each candidate: allow if any matches an
   * allow and none matches a deny.
   */
  export function agentCandidates(context: {
    readonly tenantId: string
    readonly zoneId?: string
    readonly appId?: string
    readonly agentId: string
  }): readonly Urn[] {
    const { agentId, appId, tenantId, zoneId } = context
    const levels: Parsed.Agent<string>[] = [
      { agentId, tenantId, type: ResourceType.Agent },
      ...(zoneId === undefined
        ? []
        : [{ agentId, tenantId, type: ResourceType.Agent, zoneId } as const]),
      ...(zoneId === undefined || appId === undefined
        ? []
        : [
            {
              agentId,
              appId,
              tenantId,
              type: ResourceType.Agent,
              zoneId,
            } as const,
          ]),
    ]
    return levels.map(level => from(level))
  }
}
